import type { SettlementCheck } from '@services/settlements/types'
import {
  CalendarRange,
  CheckCircle2,
  Droplets,
  Flame,
  ListPlus,
  Wallet,
  Zap
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

export type StepId =
  | 'period'
  | 'water'
  | 'heat'
  | 'other'
  | 'electricity'
  | 'advances'
  | 'summary'

export interface StepDef {
  id: StepId
  icon: LucideIcon
  /** Steps a flat may legitimately have nothing to enter for. */
  optional?: boolean
}

export const STEPS: StepDef[] = [
  { id: 'period', icon: CalendarRange },
  { id: 'water', icon: Droplets },
  { id: 'heat', icon: Flame },
  { id: 'other', icon: ListPlus },
  { id: 'electricity', icon: Zap, optional: true },
  { id: 'advances', icon: Wallet },
  { id: 'summary', icon: CheckCircle2 }
]

/**
 * Which step a failed check belongs to. The engine already knows what is
 * wrong; this turns that into "which step do I go fix it on".
 */
export function stepForCheck(check: SettlementCheck): StepId {
  const block = String(check.context?.block ?? '')

  switch (check.code) {
    case 'PERIOD_OVERLAP':
      return 'period'

    case 'MISSING_PRICE':
    case 'METER_NO_CONSUMPTION':
    case 'READING_DISCONTINUITY':
      if (block === 'ELECTRICITY') return 'electricity'
      if (block === 'HEAT_WATER') return 'heat'
      return 'water'

    case 'HEAT_PRICE_OUT_OF_RANGE':
    case 'HEAT_PRICE_NOT_DERIVED':
    case 'HEATING_LINEAR_ESTIMATE':
      return 'heat'

    case 'ELECTRICITY_INPUTS_INCOMPLETE':
      return 'electricity'

    case 'ADVANCE_SUM_MISMATCH':
    case 'ADVANCE_SPLIT_SCALED':
    case 'PERIOD_ADVANCE_MISMATCH':
    case 'UNPAID_ADVANCES':
      return 'advances'

    default:
      return 'summary'
  }
}

export type StepSeverity = 'ERROR' | 'WARNING' | null

/** The worst check severity attached to each step. */
export function stepSeverities(
  checks: SettlementCheck[]
): Record<StepId, StepSeverity> {
  const result = {} as Record<StepId, StepSeverity>
  for (const step of STEPS) result[step.id] = null

  for (const check of checks) {
    const step = stepForCheck(check)
    if (check.severity === 'ERROR') result[step] = 'ERROR'
    else if (result[step] !== 'ERROR' && check.severity === 'WARNING') {
      result[step] = 'WARNING'
    }
  }

  return result
}
